/* WRLD TAGS  ·  product card image carousel on touch screens
   Loaded on every page from layout/theme.liquid (defer), right after theme.js.
   WHY: the Concept theme only runs the card image carousel (<secondary-media>,
   Flickity, dots) on desktop, driven by mouse position, and hides it under
   768px. Jaime (Sept 10, 2026) wants the dots on mobile too. wt-collection.css
   un-hides the carousel on mobile; this script makes sure it initialises so
   the dots show. Desktop keeps the theme's hover behaviour untouched.
   RULE (Jaime, Sept 14 2026): on touch screens the card photo must NOT grab
   the swipe. Swiping anywhere on a card, photo included, has to scroll the
   product row (Trending Best Gifts and every other product carousel), easily.
   Two things make that work and both are required:
   1. the card's own photo carousel is never draggable on touch (dots are an
      indicator only);
   2. the card slider must NOT carry the class "flickity-slider" on touch.
      Concept's theme.js has a global handler: any touch that starts inside
      ".flickity-slider" gets its horizontal touchmove cancelled (that is how
      the theme stops page scroll inside real carousels). With that class on
      the card, a swipe on the photo is cancelled and the row never scrolls.
      So on touch we rename it to "wt-card-slider" and keep its layout inline.
   Do not turn draggable back on for touch and do not put the class back.
   3. on touch the theme's mousemove/mouseleave photo swap is removed and the
      carousel is pinned to the first photo (a tap fired mousemove, so the
      tapped card stayed on its second photo). Photo 1 is always the default.
   4. (Sept 14 2026, "why does this card show the back photo?") Concept's
      SecondaryMedia.init() has NO guard: it appends the photo template every
      time it is called. The theme calls it from Motion.inView and this script
      also called it, and on cards that scroll into view later both ran, so a
      second loose copy of every photo landed on top of the carousel (last
      photo = a back/inside view = what showed, dots hidden under it). Fix:
      the prototype init is wrapped so it can only run once per element, and
      every sweep removes stray .media that are not inside the carousel.
   RULES: never call init() twice on the same element (guard on children).
   Two triggers on purpose: IntersectionObserver, plus a scroll/touch fallback
   that checks the viewport by hand, because the observer does not fire in
   every embedded browser.
   DEBUG (Sept 14 2026): add ?wtdebug=1 to any URL and a white box prints the
   real measurements of the first three product cards on that device, so a
   phone problem that cannot be reproduced in an emulator can be read from a
   screenshot. Harmless without the parameter. */
(function () {
  'use strict';
  var TOUCH = window.matchMedia('(pointer: coarse)').matches;
  var pending = [];

  /* make the theme's init() idempotent (see 4 above); runs on every device, not only touch */
  try {
    var SM = window.customElements && customElements.get('secondary-media');
    if (SM && SM.prototype && typeof SM.prototype.init === 'function' && !SM.prototype.__wtGuarded) {
      var origInit = SM.prototype.init;
      SM.prototype.init = function () {
        if (this.__wtInited || this.children.length > 0) return;
        this.__wtInited = true;
        return origInit.apply(this, arguments);
      };
      SM.prototype.__wtGuarded = true;
    }
  } catch (err) {}

  /* remove a duplicate photo set if one already got in (cached page, older script) */
  function dedupe(sm) {
    try {
      if (!sm.querySelector('.flickity-viewport')) return;
      var loose = [].slice.call(sm.children).filter(function (el) { return !el.classList.contains('flickity-viewport') && !el.classList.contains('flickity-page-dots'); });
      if (loose.length) {
        loose.forEach(function (el) { el.remove(); });
        if (sm.carousel) { sm.mediaCount = sm.querySelectorAll('.flickity-cell').length; }
      }
    } catch (err) {}
  }

  /* touch: strip the class the theme's global touch handler looks for */
  function passThrough(sm) {
    dedupe(sm);
    if (!TOUCH) return;
    /* RULE (Jaime, Sept 14 2026): on touch the FIRST photo is always the one shown. Concept's
       <secondary-media> listens to mousemove (pick photo 2/3/4 by cursor x) and mouseleave (back to
       photo 1); a tap on a phone fires a synthetic mousemove and never a mouseleave, so the tapped
       card stayed on its second photo. Remove both listeners and pin the carousel to photo 1. */
    try {
      /* the theme may add its listeners after us (it inits on its own timer), so remove on every sweep */
      sm.removeEventListener('mousemove', sm.onMoveHandler);
      sm.removeEventListener('mouseleave', sm.onLeaveHandler);
    } catch (err) {}
    if (!sm.__wtNoHover) {
      sm.__wtNoHover = true;
      /* belt and braces: swallow the synthetic mouse events before the theme sees them */
      var swallow = function (e) { e.stopImmediatePropagation(); if (sm.carousel && sm.carousel.selectedIndex !== 0) { try { sm.carousel.select(0, false, true); } catch (err) {} } };
      sm.addEventListener('mousemove', swallow, true);
      sm.addEventListener('mouseenter', swallow, true);
      sm.addEventListener('mouseleave', swallow, true);
    }
    if (sm.carousel && sm.carousel.selectedIndex !== 0) {
      try { sm.carousel.select(0, false, true); } catch (err) {}
    }
    sm.querySelectorAll('.flickity-slider').forEach(function (sl) {
      sl.classList.remove('flickity-slider');
      sl.classList.add('wt-card-slider');
      sl.style.position = 'absolute';
      sl.style.width = '100%';
      sl.style.height = '100%';
    });
  }

  function ready(sm) {
    try { if (sm.children.length === 0 && typeof sm.init === 'function') sm.init(); } catch (err) {}
    if (TOUCH && sm.carousel && !sm.__wtDrag) {
      sm.__wtDrag = true;
      sm.carousel.options.draggable = false;
      if (sm.carousel.updateDraggable) sm.carousel.updateDraggable();
      try { sm.style.touchAction = 'pan-x pan-y'; } catch (err) {}
    }
    passThrough(sm);
  }

  function inViewport(el) {
    var r = el.getBoundingClientRect();
    return r.bottom > -200 && r.top < window.innerHeight + 200 && r.width > 0;
  }

  function arm(sm) {
    if (sm.__wt) return;
    sm.__wt = true;
    pending.push(sm);
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          io.disconnect();
          setTimeout(function () { ready(sm); }, 150);
        });
      }, { rootMargin: '200px 0px' });
      io.observe(sm);
    }
  }

  function sweep() {
    pending = pending.filter(function (sm) {
      if (!document.contains(sm)) return false;
      if (sm.children.length > 0 && (!TOUCH || sm.__wtDrag)) return false;
      if (inViewport(sm)) ready(sm);
      return sm.children.length === 0 || (TOUCH && !sm.__wtDrag);
    });
    hintSizes(document);
    /* in case the theme re-created a slider (resize, re-init), strip it again; dedupe runs on every device */
    document.querySelectorAll('.product-card secondary-media').forEach(passThrough);
  }


  /* Sept 18 2026 (rule 36): the theme's card <img> has a srcset (180 to 1080) but no "sizes", so browsers assume the
     photo is as wide as the screen and fetch the 1080px file (about 52 KB) for a tile that shows at 150 to 300px
     (9 to 16 KB). This tells them the real size: 45vw on phones (2 cards plus a quarter), 22vw on desktop rows
     (4.5 cards), 25vw on collection grids. Runs before the lazy images load, on every scan and sweep. */
  function hintSizes(root) {
    var list = (root && root.querySelectorAll) ? root.querySelectorAll('.product-card img[srcset]:not([data-wt-sized])') : [];
    for (var i = 0; i < list.length; i++) {
      var img = list[i];
      img.setAttribute('sizes', document.body.classList.contains('template-collection') ? '(min-width:1024px) 25vw, 45vw' : '(min-width:1024px) 22vw, 45vw');
      img.setAttribute('data-wt-sized', '1');
    }
  }
  function scan(root) {
    hintSizes(root);
    (root.querySelectorAll ? root.querySelectorAll('.product-card secondary-media') : []).forEach(arm);
  }

  var t = null;
  function throttled() { if (t) return; t = setTimeout(function () { t = null; sweep(); }, 200); }

  function start() {
    scan(document);
    new MutationObserver(function (muts) {
      muts.forEach(function (m) {
        m.addedNodes.forEach(function (n) { if (n.nodeType === 1) scan(n); });
      });
      throttled();
    }).observe(document.body, { childList: true, subtree: true });
    window.addEventListener('scroll', throttled, { passive: true });
    window.addEventListener('touchstart', throttled, { passive: true });
    window.addEventListener('resize', throttled);
    setTimeout(sweep, 600);
    setTimeout(sweep, 2000);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  /* ---- debug overlay (?wtdebug=1) ---- */
  if (/wtdebug/.test(window.location.search)) {
    var box = null;
    function r(el) {
      if (!el) return 'none';
      var b = el.getBoundingClientRect(); var cs = getComputedStyle(el);
      var tr = cs.transform === 'matrix(1, 0, 0, 1, 0, 0)' ? 'none' : cs.transform;
      return Math.round(b.left) + ',' + Math.round(b.top) + ' ' + Math.round(b.width) + 'x' + Math.round(b.height) + ' ' + cs.position + ' ov:' + cs.overflow + ' tr:' + tr;
    }
    function report() {
      var out = [];
      try {
        out.push('UA ' + navigator.userAgent.slice(0, 90));
        out.push('vw ' + window.innerWidth + 'x' + window.innerHeight + ' dpr ' + window.devicePixelRatio + ' hover:' + matchMedia('(hover:hover)').matches + ' fine:' + matchMedia('(pointer:fine)').matches + ' coarse:' + matchMedia('(pointer:coarse)').matches + ' touch:' + TOUCH);
        var links = [].slice.call(document.querySelectorAll('link[rel=stylesheet]')).map(function (l) { return l.href; }).filter(function (h) { return /wt-collection/.test(h); });
        out.push('css ' + (links[0] ? links[0].slice(-40) : 'NO wt-collection.css'));
        var scr = [].slice.call(document.scripts).map(function (s) { return s.src; }).filter(function (h) { return /wt-cards/.test(h); });
        out.push('js ' + (scr[0] ? scr[0].slice(-40) : 'NO wt-cards.js'));
        var sec = document.querySelector('.shopify-section[id*="featured-collections"]') || document;
        var cards = [].slice.call(sec.querySelectorAll('.product-card')).filter(function (c) { return c.getBoundingClientRect().width > 0; }).slice(0, 3);
        cards.forEach(function (c) {
          var tt = c.querySelector('.product-card__title'); var name = (tt ? tt.textContent : '').trim().slice(0, 16);
          var m = c.querySelector('.product-card__media');
          var a = m ? m.querySelector('a.media') : null;
          var sm = c.querySelector('secondary-media');
          var vp = c.querySelector('.flickity-viewport');
          var sl = c.querySelector('.wt-card-slider') || c.querySelector('.flickity-slider');
          var cell = c.querySelector('.flickity-cell.is-selected') || c.querySelector('.flickity-cell');
          var img = (cell || c).querySelector('img');
          var fb = c.querySelector('.product-card__carousel ~ img');
          out.push('[' + name + '] card ' + r(c) + ' disp:' + getComputedStyle(c).display);
          out.push(' media ' + r(m) + (m ? ' cont:' + getComputedStyle(m).contain : ''));
          out.push(' a.media ' + r(a) + (a ? ' pb:' + getComputedStyle(a).paddingBottom + ' h:' + getComputedStyle(a).height + ' ar:' + getComputedStyle(a).aspectRatio : ''));
          out.push(' sm ' + r(sm) + (sm ? ' cls:' + sm.className.slice(-28) : ''));
          out.push(' vp ' + r(vp) + (vp ? ' inline-h:' + vp.style.height : ''));
          out.push(' slider ' + r(sl));
          out.push(' cell ' + r(cell) + ' n=' + c.querySelectorAll('.flickity-cell').length + ' loose=' + (sm ? [].slice.call(sm.children).filter(function (el) { return !/flickity-(viewport|page-dots)/.test(el.className); }).length : '?') + ' dots=' + (sm ? sm.querySelectorAll('.flickity-page-dots > *').length : '?'));
          out.push(' img ' + r(img) + (img ? ' nat:' + img.naturalWidth + 'x' + img.naturalHeight + ' fit:' + getComputedStyle(img).objectFit + ' w:' + getComputedStyle(img).width + ' h:' + getComputedStyle(img).height : ''));
          out.push(' fallback img ' + r(fb));
        });
      } catch (e) { out.push('ERR ' + e.message); }
      if (!box) {
        box = document.createElement('pre');
        box.style.cssText = 'position:fixed;left:0;top:0;z-index:2147483647;background:rgba(255,255,255,.97);color:#000;font:10px/1.3 monospace;padding:6px;margin:0;max-width:100vw;box-sizing:border-box;white-space:pre-wrap;word-break:break-all;max-height:100vh;overflow:auto;pointer-events:none';
        document.body.appendChild(box);
      }
      box.textContent = out.join('\n');
    }
    /* ?wtdebug=twill (or any word) taps the tab whose label contains that word first */
    var want = (window.location.search.match(/wtdebug=([^&]*)/) || [])[1] || '';
    want = decodeURIComponent(want).toLowerCase();
    if (want && want !== '1') {
      setTimeout(function () {
        var tab = [].slice.call(document.querySelectorAll('.tab__item')).filter(function (b) { return b.textContent.toLowerCase().indexOf(want) !== -1; })[0];
        if (tab) { tab.scrollIntoView(); tab.click(); }
      }, 1500);
    }
    setTimeout(report, 4500);
    setInterval(report, 2500);
  }
})();